import { findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { useState, useEffect } from 'react';
import moreVerticalFill from '@iconify/icons-eva/more-vertical-fill';
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles';
import { Box, Grid, Card, IconButton, Typography, CardContent, Paper, Chip, Stack, Divider, TextField, Autocomplete, CircularProgress, Avatar, Pagination } from '@material-ui/core';
// utils
import { fDate } from '../../../utils/formatTime';
import LightboxModal from 'src/components/LightboxModal';
import { gallery } from './data';
import { MIconButton } from 'src/components/@material-extend';
import { PlayArrow } from '@material-ui/icons';
import { useNavigate } from 'react-router';

// ----------------------------------------------------------------------

const CaptionStyle = styled(CardContent)(({ theme }) => ({
  bottom: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  backdropFilter: 'blur(3px)',
  WebkitBackdropFilter: 'blur(3px)', // Fix on Mobile
  justifyContent: 'space-between',
  color: theme.palette.common.white,
  backgroundColor: alpha(theme.palette.grey[900], 0.72),
  borderBottomLeftRadius: theme.shape.borderRadiusMd,
  borderBottomRightRadius: theme.shape.borderRadiusMd,
}));

const GalleryImgStyle = styled('img')({
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
});

const GradientOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: 'linear-gradient(180deg,rgba(30,127,190,0) 0%,rgba(30,127,190,0.1) 15%,rgba(30,127,190,0.25) 45%,rgba(30,127,190,0.35) 65%,rgba(30,127,190,0.5) 100%)',
  zIndex: 1,
});

const CustomTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#97999b',
    },
    '&:hover fieldset': {
      borderColor: '#000048',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#6aa2dc',
    },
    '& input': {
      color: '#000048',
      fontSize: '18px',
      fontWeight: '500',
      fontFamily: '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
    },
    '&::placeholder': {
      color: '#000048',
      fontSize: '18px',
      fontWeight: '500',
      fontFamily: '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
      opacity: 1, 
    },
  },
  '& .MuiInputLabel-root': {
    color: '#000048',
    fontSize: '17px',
    marginTop: '-3px',
    fontWeight: '400',
    fontFamily: '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
  },
});

// ----------------------------------------------------------------------

GalleryItem.propTypes = {
  caseStudy: PropTypes.object,
  onOpenLightbox: PropTypes.func,
  isLoading: PropTypes.bool,
};

function GalleryItem({ caseStudy, onOpenLightbox, isLoading }) {
  const { id, imageUrl, title1, icon, title, title2, country, imageCarousal } = caseStudy;
  const navigate = useNavigate();

  const handleNavigation = () => {
    navigate(`/case_study/${title2}`, { state: { caseStudy } });
  };

  return (
    <Paper onClick={handleNavigation} sx={{ cursor: 'pointer', height: '455px', position: 'relative', border: '4px solid #019bdc', borderRadius: 0.1, transition: 'transform 0.2s',
        '&:hover': {
          transform: 'scale(1.01)',
          '& .view-text': {
            display: 'block',
            opacity: 1,
            WebkitTransform: 'translateY(0)',
            transform: 'translateY(0)',
            WebkitTransition: 'all .5s cubic-bezier(.77,0,.175,1)',
            transition: 'all .5s cubic-bezier(.77,0,.175,1)',
          },
        },
      }}>
      {isLoading ? (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <GalleryImgStyle alt="gallery image" src={imageUrl} />
          <GradientOverlay className='overlay' />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 2,
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}
          >
            <Typography
              sx={{
                textAlign: 'left',
                pl: 3,
                pr: 2,
                fontSize: '27px',
                fontWeight: 600,
                color: '#fff',
                lineHeight: 1.2,
                fontFamily:
                  '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
              }}
              variant="subtitle1"
            >
              {title1}
            </Typography>
            <Divider sx={{ borderColor: 'red' }} />
          </Box>
          <Divider sx={{
            zIndex: 2,
            borderColor: '#cccccc',
            position: 'absolute',
            top: 0,
            left: 15,
            right: 15,
            bottom: 85,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }} />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 2,
              backdropFilter: 'blur(0.8px)',
              WebkitBackdropFilter: 'blur(0.5px)',
            }}
          >
            <Chip
              icon={<Stack className={icon} sx={{ fontSize: '1.2rem', }} />}
              label={title.toUpperCase()}
              sx={{
                backgroundColor: '#019bdc',
                borderRadius: 0.1,
                color: '#fff',
                mb: 1,
                fontFamily:
                  '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                fontWeight: '600',
                fontSize: '15px',
                padding: 1,
                alignContent: 'center',
                textAlign: 'center',
                mt: 3,
                textTransform: 'uppercase',
              }}
            />
          </Box>
          {country && (<Stack
            sx={{
              position: 'absolute',
              bottom: -8,
              // mt:2,
              right: 10,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              zIndex: 2,
            }}
          >

            <Avatar src={country} alt='country'/>
            <Typography
              sx={{
                fontSize: '17px',
                fontWeight: 600,
                color: '#fff',
                lineHeight: 1.2,
                mb: 2,
                mt: 0.6,
                fontFamily: '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                WebkitTransform: 'translateY(-0.5rem)',
                transform: 'translateY(-0.5rem)',
                WebkitTransition: 'all .5s cubic-bezier(.77,0,.175,1)',
                transition: 'all .5s cubic-bezier(.77,0,.175,1)',
                opacity: 0,
              }}
              className='view-text'
              variant="subtitle1"
            >
              View
            </Typography>
          </Stack>)}
        </>
      )}
    </Paper>
  );
}

function SearchNotFound() {
  return (
    <Box sx={{ textAlign: 'center', }}>
      <Typography sx={{
        color: '#000048',
        fontSize: '27px',
        fontWeight: '400',
        fontFamily: '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',}}>
        No case studies found
      </Typography>
    </Box>
  );
}

export default function CaseStudyListItems() {
  const [openLightbox, setOpenLightbox] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectCategory, setSelectCategory] = useState(null);
  const [selectIndustry, setSelectIndustry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 9; 
  const imagesLightbox = gallery.map((img) => img.imageUrl);

  const handleOpenLightbox = (url) => {
    const selectedImage = findIndex(imagesLightbox, (index) => index === url);
    setOpenLightbox(true);
    setSelectedImage(selectedImage);
  };

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [searchTerm, selectCategory, selectIndustry]);

  const getUniqueOptions = (gallery, key) => {
    const uniqueValues = new Set();
    return gallery.filter((item) => {
      if (item.id === 4 || uniqueValues.has(item[key]) || !item[key]) return false;
      uniqueValues.add(item[key]);
      return true;
    });
  };

  const getAllIndustryOptions = (gallery) => {
    const industries = new Set();
    gallery.forEach((item) => {
      if (item.industry) industries.add(item.industry);
      if (item.industry1) industries.add(item.industry1);
      if (item.industry2) industries.add(item.industry2);
      if (item.industry3) industries.add(item.industry3);
      if (item.industry4) industries.add(item.industry4);
    });
    return Array.from(industries).map((industry) => ({ industry }));
  };

  const filteredGallery = gallery.filter(caseStudy => {
    if (caseStudy.id === 4) return false; // Exclude case studies with id=4
    const searchWords = searchTerm.toLowerCase().split(' ');
    const matchesSearchTerm = searchWords.every(word => 
      (caseStudy.title1 && caseStudy.title1.toLowerCase().includes(word)) || 
      (caseStudy.title && caseStudy.title.toLowerCase().includes(word)) || 
      (caseStudy.industry && caseStudy.industry.toLowerCase().includes(word)) ||
      (caseStudy.industry1 && caseStudy.industry1.toLowerCase().includes(word)) ||
      (caseStudy.industry2 && caseStudy.industry2.toLowerCase().includes(word)) ||
      (caseStudy.industry3 && caseStudy.industry3.toLowerCase().includes(word)) ||
      (caseStudy.industry4 && caseStudy.industry4.toLowerCase().includes(word))
    );
    const matchesCategory = selectCategory === null || caseStudy.title === selectCategory.title;
    const matchesIndustry = selectIndustry === null || [caseStudy.industry, caseStudy.industry1, caseStudy.industry2, caseStudy.industry3, caseStudy.industry4].includes(selectIndustry.industry);
    return matchesCategory && matchesSearchTerm && matchesIndustry;
  });

  const paginatedGallery = filteredGallery.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, filteredGallery.length);

  return (
    <Box sx={{ mt: 5, mb: 4 }}>
      <Grid sx={{ p: 3 }}>
        <Grid container spacing={2} sx={{ mb: 8, mt: -5 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              fullWidth
              options={getUniqueOptions(gallery, 'title')}
              getOptionLabel={(option) => option.title}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  placeholder="Select Category"
                  InputLabelProps={{
                    style: {
                      color: '#000048',
                      fontSize: '17px',
                      fontWeight: '400',
                      fontFamily: '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              value={selectCategory}
              onChange={(event, newValue) => {
                setSelectCategory(newValue);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              fullWidth
              options={getAllIndustryOptions(gallery)}
              getOptionLabel={(option) => option.industry}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  placeholder="Select Industry"
                  InputLabelProps={{
                    style: {
                      color: '#000048',
                      fontSize: '17px',
                      fontWeight: '400',
                      fontFamily: '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              value={selectIndustry}
              onChange={(event, newValue) => {
                setSelectIndustry(newValue);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <CustomTextField
              fullWidth
              InputLabelProps={{
                style: {
                  color: '#000048',
                  fontSize: '17px',
                  fontFamily: '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
                  fontWeight: '400',
                  backgroundColor: '#f5f5f2',
                },
              }}
              placeholder='Search Case Studies'
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when search term changes
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#97999b',
                  },
                  '&:hover fieldset': {
                    borderColor: '#000048',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6aa2dc',
                  },
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          {isLoading ? (
            <Grid item xs={12} display="flex" justifyContent="center">
              <CircularProgress />
            </Grid>
          ) : paginatedGallery.length > 0 ? (
            paginatedGallery.map((caseStudy) => (
              <Grid key={caseStudy.id} item xs={12} sm={6} md={4}>
                <GalleryItem caseStudy={caseStudy} onOpenLightbox={handleOpenLightbox} isLoading={isLoading} />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <SearchNotFound />
            </Grid>
          )}
        </Grid>
        {!isLoading && paginatedGallery.length > 0 && (
          <Box sx={{ mt: 6, mb: -6, display: 'flex', justifyContent: 'flex-start', flexDirection: 'column' }}>
            <Typography
              sx={{
                textAlign: 'left',
                fontSize: '22px',
                fontWeight: 400,
                pl: 1,
                mb: 3,
                color: '#000',
                lineHeight: 1.2,
                fontFamily:
                  '"Gellix",system-ui,-apple-system,"Segoe UI",Roboto,"Helvetica Neue","Noto Sans","Liberation Sans",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
              }}
              variant="subtitle1"
            >
              Displaying {startItem} to {endItem} of {filteredGallery.length}
            </Typography>
            <Pagination
              count={Math.ceil(filteredGallery.length / itemsPerPage)}
              variant="outlined"
              shape="rounded"
              color="primary"
              size="large"
              page={currentPage}
              onChange={(event, value) => setCurrentPage(value)}
            />
          </Box>
        )}
        <LightboxModal
          images={imagesLightbox}
          photoIndex={selectedImage}
          setPhotoIndex={setSelectedImage}
          isOpen={openLightbox}
          onClose={() => setOpenLightbox(false)}
        />
      </Grid>
    </Box>
  );
}
